import React, { useEffect, useState } from "react";
import { fetchMarketingList, fetchMarketingById, updateMarketing } from "../../../../api/candidiate";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../../../../components/Loader";
import { formatDate, formatDateCST, formatTimeCST } from "../../../../utlis";

const MarketingHistory = () => {
  const {
    firstName,
    lastName,
    phone
  } = useSelector((state) => state.personal.basic);

  const { SSN, salesPerson: professionalSalesPerson } = useSelector((state) => state.personal.professional);

  const [marketingHistory, setMarketingHistory] = useState({
    marketingEmail: "",
    marketingStartDate: "",
    marketingEndDate: "",
    marketingStatus: "",
    salesPersonName: "",
    linkedInID: "",
    skypeID: "",
  });

  const [activeRecordId, setActiveRecordId] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [dateError, setDateError] = useState("");
  const [audit, setAudit] = useState(null);
  const [originalValues, setOriginalValues] = useState({ linkedInID: "", skypeID: "" });
  const candidateId = useSelector(state => state.personal.candidateId);

  const isActive = marketingHistory.marketingStatus === "Active";

  // Calculate on bench days from start date to end date (or today if no end date)
  const calculateOnBenchDays = () => {
    if (!marketingHistory.marketingStartDate) return "";
    const start = new Date(marketingHistory.marketingStartDate);
    const end = marketingHistory.marketingEndDate ? new Date(marketingHistory.marketingEndDate) : new Date();
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "";
    const diffMs = end - start;
    const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
    return `${days} day${days !== 1 ? 's' : ''}`;
  };

  const onBenchFor = calculateOnBenchDays();

  // Convert API date (ISO or MM/DD/YYYY) to YYYY-MM-DD for date input
  const toInputDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchActiveRecord = async () => {
    if (!candidateId) return;
    setLoading(true);
    try {
      const listRes = await fetchMarketingList({ candidateId, pageNum: 1, pageSize: 100 });
      const records = listRes.data?.response?.data || [];
      if (records.length === 0) return;
      const activeListItem = records.find(r => r.status === "Active") || records[0];
      const recordId = activeListItem.record_id;
      if (!recordId) return;

      const detailRes = await fetchMarketingById(recordId, candidateId);
      const detailRecords = detailRes.data?.response?.data || [];
      const active = detailRecords[0] || activeListItem;

      setActiveRecordId(active.record_id || null);
      setAudit({
        createdBy: active.created_by || '',
        dateCreated: active.created || '',
        updatedBy: active.updated_by || '',
        lastUpdated: active.updated || '',
      });
      const linkedIn = active.linkedin || "";
      const skype = active.skype || "";
      setOriginalValues({ linkedInID: linkedIn, skypeID: skype });
      setMarketingHistory({
        marketingEmail: active.email || "",
        marketingStartDate: toInputDate(active.start_date),
        marketingEndDate: toInputDate(active.end_date),
        marketingStatus: active.status || "",
        salesPersonName: active.sales_person_name || "",
        linkedInID: linkedIn,
        skypeID: skype,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch active marketing history record on load
  useEffect(() => {
    if (candidateId) {
      fetchActiveRecord();
    }
  }, [candidateId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMarketingHistory((prev) => ({ ...prev, [name]: value }));
  };

  const handleEndDateChange = (e) => {
    const endDate = e.target.value;
    setMarketingHistory((prev) => ({ ...prev, marketingEndDate: endDate }));

    if (endDate && marketingHistory.marketingStartDate && endDate < marketingHistory.marketingStartDate) {
      setDateError("End date must be greater than or equal to start date");
    } else {
      setDateError("");
    }
  };

  const hasFieldChanges = isActive && (
    marketingHistory.linkedInID !== originalValues.linkedInID ||
    marketingHistory.skypeID !== originalValues.skypeID
  );
  const hasEndDate = isActive && marketingHistory.marketingEndDate && !dateError;
  const isSaveEnabled = hasEndDate || hasFieldChanges;

  const handleSubmit = async () => {
    if (!activeRecordId) return;
    setLoading(true);
    try {
      const payload = {
        candidateId: String(candidateId),
        linkedin: marketingHistory.linkedInID,
        skype: marketingHistory.skypeID,
      };

      // Only set end date and change status to InActive if end date is provided
      if (marketingHistory.marketingEndDate) {
        payload.endDate = formatDate(marketingHistory.marketingEndDate);
        payload.status = "InActive";
      }

      await updateMarketing(activeRecordId, payload);

      if (marketingHistory.marketingEndDate) {
        toast.success('Marketing status updated to InActive');
      } else {
        toast.success('Marketing record updated successfully');
      }
      setDateError("");
      // Re-fetch to get latest data from backend
      await fetchActiveRecord();
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      toast.error(data?.details || data?.message || 'Failed to update marketing record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {isLoading && <Loader />}
      <div className="flex flex-col gap-y-7 mt-5 text-sm font-normal text-text-hint">
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">Candidate Name</label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg">
              <input
                value={`${firstName} ${lastName}`}
                disabled={true}
                placeholder="Candidate Name"
                className="outline-none w-full text-black bg-transparent"
              />
            </div>
          </div>
          <div className="">
            <label className="">Candidate Phone Number</label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg">
              <input
                value={phone}
                disabled={true}
                placeholder="Candidate Phone Number"
                className="outline-none w-full text-black bg-transparent"
              />
            </div>
          </div>
          <div className="">
            <label className="">Marketing Email</label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg">
              <input
                value={marketingHistory.marketingEmail}
                disabled={true}
                placeholder="Marketing Email"
                className="outline-none w-full text-black bg-transparent"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div className="max-w-xs">
            <label className="">Marketing Start Date</label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                value={marketingHistory.marketingStartDate}
                type="date"
                disabled={true}
                className="outline-none border-none w-full text-black bg-transparent"
              />
            </div>
          </div>
          <div className="max-w-xs">
            <label className="">Marketing End Date</label>
            <div className={`flex justify-center items-center px-3 py-2 mt-1 border rounded-lg h-10 ${isActive ? 'border-secondary-500' : 'border-neutral-500'}`}>
              <input
                value={marketingHistory.marketingEndDate}
                onChange={handleEndDateChange}
                type="date"
                disabled={!isActive}
                min={marketingHistory.marketingStartDate || undefined}
                className={`outline-none border-none w-full text-black bg-transparent ${isActive ? '' : 'cursor-not-allowed'}`}
              />
            </div>
            {dateError && (
              <p className="text-red-500 text-xs mt-1">{dateError}</p>
            )}
          </div>
          <div className="">
            <label className="">Marketing Status</label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg">
              <input
                value={marketingHistory.marketingStatus}
                disabled={true}
                placeholder="Marketing Status"
                className="outline-none w-full text-black bg-transparent"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">Sales Person</label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg">
              <input
                value={marketingHistory.salesPersonName}
                disabled={true}
                placeholder="Sales Person"
                className="outline-none w-full text-black bg-transparent"
              />
            </div>
          </div>
          <div className="">
            <label className="">SSN</label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg">
              <input
                value={SSN ? `***-**-${SSN.slice(-4)}` : ''}
                disabled={true}
                placeholder="SSN"
                className="outline-none w-full text-black bg-transparent"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">LinkedIn</label>
            <div className={`flex justify-center items-center px-3 py-2 mt-1 border rounded-lg ${isActive ? 'border-secondary-500' : 'border-neutral-500'}`}>
              <input
                value={marketingHistory.linkedInID}
                onChange={handleChange}
                name="linkedInID"
                disabled={!isActive}
                placeholder="LinkedIn"
                className={`outline-none w-full text-black bg-transparent ${!isActive ? 'cursor-not-allowed' : ''}`}
              />
            </div>
          </div>
          <div className="">
            <label className="">SkypeID</label>
            <div className={`flex justify-center items-center px-3 py-2 mt-1 border rounded-lg ${isActive ? 'border-secondary-500' : 'border-neutral-500'}`}>
              <input
                value={marketingHistory.skypeID}
                onChange={handleChange}
                name="skypeID"
                disabled={!isActive}
                placeholder="SkypeID"
                className={`outline-none w-full text-black bg-transparent ${!isActive ? 'cursor-not-allowed' : ''}`}
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">On bench for</label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg">
              <input
                value={onBenchFor}
                disabled={true}
                placeholder="On bench for"
                className="outline-none w-full text-black bg-transparent"
              />
            </div>
          </div>
        </div>
        {/* Audit Footer */}
        {activeRecordId && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Created By</label>
              <p className="text-sm text-gray-600">{audit?.createdBy || 'N/A'}</p>
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Date Created</label>
              <p className="text-sm text-gray-600">
                {audit?.dateCreated ? formatDateCST(audit.dateCreated) : 'N/A'}
              </p>
              {audit?.dateCreated && (
                <p className="text-xs text-gray-500">{formatTimeCST(audit.dateCreated)}</p>
              )}
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Updated By</label>
              <p className="text-sm text-gray-600">{audit?.updatedBy || 'N/A'}</p>
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Last Updated</label>
              <p className="text-sm text-gray-600">
                {audit?.lastUpdated ? formatDateCST(audit.lastUpdated) : 'N/A'}
              </p>
              {audit?.lastUpdated && (
                <p className="text-xs text-gray-500">{formatTimeCST(audit.lastUpdated)}</p>
              )}
            </div>
          </div>
        )}
      </div>
      {isActive && (
        <div className="w-full py-10 px-8 flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={!isSaveEnabled}
            className={`py-2 px-5 rounded-full text-base ${isSaveEnabled ? 'bg-secondary-700 text-white' : 'bg-neutral-200 text-gray-400 cursor-not-allowed'}`}
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
};

export default MarketingHistory;
